import { parseAddressPath } from 'data/addresses';
import { ROOT_PATH_SUFFIX } from 'data/addresses/go-search/types';
import { AddressType } from '@zoocasa/go-search';
import type { ParsedAddressPath } from 'data/addresses/go-search/addressPathParser';

/**
 * Represents the type of location based on URL path parsing.
 * Uses AddressType directly, with 'unknown' as fallback.
 */
export type LocationType = AddressType | 'unknown';

/**
 * Detects the location type from a URL path.
 * 
 * @param urlPath - The URL path to analyze (e.g., "/toronto-on-real-estate/university-of-toronto")
 * @returns The detected location type (AddressType enum value or 'unknown')
 */
export function detectLocationType(urlPath: string): LocationType {
  if (!urlPath) {
    return 'unknown';
  }

  const parsedPaths = parseAddressPath(urlPath, ROOT_PATH_SUFFIX);
  const parsedPath = parsedPaths?.[0];

  if (!parsedPath) {
    return 'unknown';
  }

  return parsedPath.type;
}

/**
 * Extracts the city slug from a URL path based on the location type.
 * 
 * @param urlPath - The URL path to extract from
 * @param locationType - The detected location type
 * @param parsedPath - Optional parsed path data for more accurate extraction
 * @returns The city slug (e.g., "toronto-on")
 */
export function extractCitySlugFromPath(
  urlPath: string,
  locationType: LocationType,
  parsedPath?: ParsedAddressPath
): string {
  // If location type is unknown, we can't extract a valid city slug
  if (locationType === 'unknown') {
    return '';
  }

  // For neighbourhood or address, try to get slug from parsed path first
  const isNeighbourhood = locationType === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD || 
                          locationType === AddressType.ADDRESS_TYPE_ADDRESS;
  if (isNeighbourhood && parsedPath?.subDivision?.slug) {
    return parsedPath.subDivision.slug;
  }

  // Fallback: extract from first segment of URL path
  const pathSegments = urlPath.replace(/^\//, '').split('/');
  const firstSegment = pathSegments[0] || '';
  
  // Remove the root path suffix (e.g., "-real-estate")
  return firstSegment.replace(`-${ROOT_PATH_SUFFIX}`, '');
}

/**
 * Detects location type and extracts relevant information from a URL path.
 * 
 * @param urlPath - The URL path to analyze
 * @returns Object containing location type, city slug, and parsed path data
 */
export function analyzeLocationPath(urlPath: string): {
  locationType: LocationType;
  citySlug: string;
  parsedPath?: ParsedAddressPath;
} {
  const parsedPaths = parseAddressPath(urlPath, ROOT_PATH_SUFFIX);
  const parsedPath = parsedPaths?.[0];
  const locationType = detectLocationType(urlPath);
  const citySlug = extractCitySlugFromPath(urlPath, locationType, parsedPath);

  return {
    locationType,
    citySlug,
    parsedPath,
  };
}

